import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom'; // Import Link

// Main component for the Background Remover page
const BackgroundRemoverPage = () => {
    // State variables for managing the application's data and UI
    const [selectedImage, setSelectedImage] = useState(null); // Stores the original image file
    const [previewImage, setPreviewImage] = useState(null); // Stores the URL for the original image preview
    const [processedImage, setProcessedImage] = useState(null); // Stores the URL for the processed image
    const [isLoading, setIsLoading] = useState(false); // Indicates if the background removal is in progress
    const [error, setError] = useState(null); // Stores any error messages
    const fileInputRef = useRef(null); // Ref for the hidden file input element

    // remove.bg API URL
    const REMOVE_BG_API_URL = 'https://api.remove.bg/v1.0/removebg';
    // Directly using the provided API key (ensure this is handled securely in a real application)
    const REMOVE_BG_API_KEY = 'pGZcTtD2v8uWy6iKiWWEysaN'; // Make sure this key is correct and secure

    // Function to handle file selection
    const handleImageChange = (event) => {
        const file = event.target.files[0]; // Get the first selected file

        if (file) {
            setSelectedImage(file); // Store the file itself
            setPreviewImage(URL.createObjectURL(file)); // Create a URL for immediate preview
            setProcessedImage(null); // Clear any previously processed image
            setError(null); // Clear previous errors
        }
    };

    // Function to trigger the file input click
    const handleUploadClick = () => {
        fileInputRef.current.click();
    };

    // Function to send the image to remove.bg API for background removal
    const removeBackground = async () => {
        if (!selectedImage) {
            setError('Please select an image first.');
            return;
        }

        setIsLoading(true); // Set loading state to true
        setProcessedImage(null); // Clear previous processed image
        setError(null); // Clear previous errors

        const formData = new FormData();
        formData.append('image_file', selectedImage); // Append the image file
        formData.append('size', 'auto'); // Request automatic size detection
        formData.append('output_format', 'png'); // Request PNG format for transparency

        try {
            const response = await fetch(REMOVE_BG_API_URL, {
                method: 'POST',
                headers: {
                    'X-Api-Key': REMOVE_BG_API_KEY, // Add the API key to the headers
                    'Accept': 'application/json' // Explicitly request a JSON response
                },
                body: formData,
            });

            if (!response.ok) {
                // If response is not OK (e.g., 400, 500 status)
                const errorData = await response.json();
                throw new Error(errorData.errors?.[0]?.title || 'Something went wrong with the remove.bg API.');
            }

            const data = await response.json();
            if (data.data && data.data.result_b64) {
                // Set the processed image URL (base64 encoded)
                setProcessedImage(`data:image/png;base64,${data.data.result_b64}`);
            } else {
                throw new Error('No processed image received from remove.bg API.');
            }
        } catch (err) {
            console.error('Error removing background:', err);
            setError(`Failed to remove background: ${err.message}`);
        } finally {
            setIsLoading(false); // Set loading state to false regardless of success or failure
        }
    };

    return (
        <div className="min-h-screen bg-[#0A1128] text-[#F8F8F8] font-sans overflow-x-hidden">

            {/* Section 1: Hero/Introduction - Background with image (similar to PosterGallery) */}
            <section
                className="py-20 text-center px-6 relative overflow-hidden bg-cover bg-center bg-no-repeat"
                style={{
                    backgroundImage: `url('https://i.imgur.com/pn2Gjr9.png')`, // Consistent background image
                }}
            >
                {/* Overlay for dark background and improved text readability */}
                <div className="absolute inset-0 bg-black bg-opacity-20 z-0"></div>
                <div className="container mx-auto relative z-10 flex flex-col items-center justify-center">
                    {/* Added a div for the rounded black overlay */}
                    <div className="bg-black bg-opacity-60 p-8 rounded-lg max-w-4xl w-full">
                        {/* Back button at the top */}
                        <div className="w-full flex justify-start mb-12">
                            <Link to="/graphics-design" className="px-7 py-3 bg-[#C9B072] text-[#0A1128] font-bold text-lg rounded-full shadow-xl transition duration-300 transform hover:scale-105 hover:bg-opacity-90 hover:shadow-2xl">
                                &larr; Back to Graphics & Design
                            </Link>
                        </div>

                        {/* Page Title */}
                        <h2 className="text-5xl md:text-6xl font-extrabold text-[#C9B072] mb-12 drop-shadow-lg leading-tight">
                            AI Background Remover
                        </h2>
                        <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
                            Effortlessly remove backgrounds from your images with a single click, powered by AI.
                        </p>
                    </div>
                </div>
            </section>

            {/* Main content section for the tool */}
            <section className="py-20 bg-gray-100 border-t border-[#C9B072] text-center px-6 flex justify-center items-start min-h-[50vh]">
                <div className="bg-white rounded-3xl shadow-2xl p-8 w-full max-w-5xl flex flex-col md:flex-row gap-8 items-stretch">
                    {/* Left Section: Upload and Original Image */}
                    <div className="flex-1 flex flex-col items-center justify-center p-6 bg-gray-50 rounded-2xl shadow-inner border border-gray-200">
                        <h2 className="text-3xl font-bold text-[#0A1128] mb-6 text-center">
                            Upload Image
                        </h2>
                        <p className="text-gray-600 mb-8 text-center">
                            Select an image to remove its background.
                        </p>

                        <input
                            type="file"
                            accept="image/*"
                            onChange={handleImageChange}
                            ref={fileInputRef}
                            className="hidden" // Hide the default file input
                        />

                        <button
                            onClick={handleUploadClick}
                            className="bg-[#C9B072] text-[#0A1128] font-semibold py-3 px-8 rounded-full shadow-lg transform transition duration-300 ease-in-out hover:scale-105 mb-6 focus:outline-none focus:ring-4 focus:ring-[#C9B072] focus:ring-opacity-50"
                        >
                            {selectedImage ? 'Change Image' : 'Select Image'}
                        </button>

                        {previewImage && (
                            <div className="relative w-full max-w-sm h-64 border-2 border-dashed border-gray-300 rounded-xl overflow-hidden mb-6 flex items-center justify-center bg-gray-200">
                                <img
                                    src={previewImage}
                                    alt="Original Preview"
                                    className="max-w-full max-h-full object-contain rounded-lg"
                                />
                            </div>
                        )}

                        {selectedImage && (
                            <button
                                onClick={removeBackground}
                                disabled={isLoading}
                                className={`w-full max-w-sm py-3 px-6 rounded-full font-semibold text-[#0A1128] transition duration-300 ease-in-out transform ${
                                    isLoading
                                        ? 'bg-gray-300 cursor-not-allowed'
                                        : 'bg-[#C9B072] hover:bg-opacity-90 shadow-lg hover:scale-105 focus:outline-none focus:ring-4 focus:ring-[#C9B072] focus:ring-opacity-50'
                                }`}
                            >
                                {isLoading ? (
                                    <span className="flex items-center justify-center">
                                        <svg
                                            className="animate-spin -ml-1 mr-3 h-5 w-5 text-[#0A1128]"
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                        >
                                            <circle
                                                className="opacity-25"
                                                cx="12"
                                                cy="12"
                                                r="10"
                                                stroke="currentColor"
                                                strokeWidth="4"
                                            ></circle>
                                            <path
                                                className="opacity-75"
                                                fill="currentColor"
                                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                            ></path>
                                        </svg>
                                        Processing...
                                    </span>
                                ) : (
                                    'Remove Background'
                                )}
                            </button>
                        )}

                        {error && (
                            <p className="text-red-600 mt-4 text-center text-sm">{error}</p>
                        )}
                    </div>

                    {/* Right Section: Processed Image Display */}
                    <div className="flex-1 flex flex-col items-center justify-center p-6 bg-gray-50 rounded-2xl shadow-inner border border-gray-200">
                        <h2 className="text-3xl font-bold text-[#0A1128] mb-6 text-center">
                            Result
                        </h2>
                        <p className="text-gray-600 mb-8 text-center">
                            Your image with the background removed will appear here.
                        </p>

                        <div className="relative w-full max-w-sm h-64 border-2 border-dashed border-gray-300 rounded-xl overflow-hidden flex items-center justify-center bg-gray-200">
                            {processedImage ? (
                                <img
                                    src={processedImage}
                                    alt="Processed"
                                    className="max-w-full max-h-full object-contain rounded-lg animate-fade-in" // Apply fade-in animation
                                />
                            ) : (
                                <p className="text-gray-500">No image processed yet.</p>
                            )}
                        </div>

                        {processedImage && (
                            <a
                                href={processedImage}
                                download="background-removed.png"
                                className="mt-6 bg-[#C9B072] text-[#0A1128] font-semibold py-3 px-8 rounded-full shadow-lg transform transition duration-300 ease-in-out hover:scale-105 focus:outline-none focus:ring-4 focus:ring-[#C9B072] focus:ring-opacity-50"
                            >
                                Download Image
                            </a>
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default BackgroundRemoverPage;
